import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { OrderActions } from '../../hooks/useOrderActions'
import type { DealerOrderItem } from '../../types'
import { PhoneCopy } from './OrderParts'
import { CheckIcon, CloseIcon, PencilIcon } from './icons'

interface Props {
  item: DealerOrderItem
  actions: OrderActions
  // compact — в строке таблицы/карточке: иконки вместо кнопок, без подсказки
  compact?: boolean
}

// Номер клиента: копирование и правка (механик мог не ввести номер или ошибиться)
export default function PhoneEditor({ item, actions, compact = false }: Props) {
  const { t } = useTranslation()
  const [editing, setEditing] = useState(false)
  const [value, setValue] = useState(item.clientPhone ?? '')
  const [error, setError] = useState<string | null>(null)
  const busy = actions.isBusy(item.id)

  // другой заказ — выходим из режима правки
  useEffect(() => {
    setEditing(false)
    setError(null)
  }, [item.id])

  // клики внутри редактора не должны открывать панель заказа (клик по строке)
  const stop = (e: React.SyntheticEvent) => e.stopPropagation()

  function startEdit(e: React.MouseEvent) {
    e.stopPropagation()
    setValue(item.clientPhone ?? '')
    setError(null)
    setEditing(true)
  }

  function cancel() {
    setEditing(false)
    setError(null)
  }

  async function save(e: React.FormEvent) {
    e.preventDefault()
    e.stopPropagation()
    const err = await actions.savePhone(item, value)
    if (err) setError(err)
    else setEditing(false)
  }

  if (!editing) {
    if (!item.clientPhone) {
      return (
        <button type="button" className="dealer-text-btn dealer-text-btn--flush" onClick={startEdit}>
          {t('dealer.orders.add_phone')}
        </button>
      )
    }
    return (
      <span className="dealer-phone-editor">
        <PhoneCopy item={item} actions={actions} />
        {compact ? (
          <button
            type="button"
            className="dealer-icon-btn"
            onClick={startEdit}
            title={t('dealer.orders.edit_phone')}
            aria-label={t('dealer.orders.edit_phone')}
          >
            <PencilIcon />
          </button>
        ) : (
          <button type="button" className="dealer-text-btn" onClick={startEdit}>
            {t('dealer.orders.edit_phone')}
          </button>
        )}
      </span>
    )
  }

  return (
    <form
      className={`dealer-phone-editor dealer-phone-editor--editing${compact ? ' dealer-phone-editor--compact' : ''}`}
      onSubmit={save}
      onClick={stop}
      noValidate
    >
      <span className="dealer-phone-editor__row">
        <input
          className={`dealer-input dealer-phone-editor__input${error ? ' dealer-input--error' : ''}`}
          type="tel"
          inputMode="tel"
          autoComplete="off"
          placeholder={t('dealer.orders.phone_placeholder')}
          value={value}
          onChange={(e) => { setValue(e.target.value); setError(null) }}
          onKeyDown={(e) => { if (e.key === 'Escape') { e.stopPropagation(); cancel() } }}
          disabled={busy}
          autoFocus
          aria-label={t('dealer.orders.col_customer')}
        />
        {compact && (
          <>
            <button
              type="submit"
              className="dealer-icon-btn dealer-icon-btn--primary"
              disabled={busy}
              title={t('dealer.orders.save_phone')}
              aria-label={t('dealer.orders.save_phone')}
            >
              <CheckIcon />
            </button>
            <button
              type="button"
              className="dealer-icon-btn"
              onClick={cancel}
              disabled={busy}
              title={t('dealer.orders.cancel_edit')}
              aria-label={t('dealer.orders.cancel_edit')}
            >
              <CloseIcon />
            </button>
          </>
        )}
      </span>
      {!compact && (
        <span className="dealer-phone-editor__actions">
          <button type="submit" className="dealer-btn dealer-btn--primary dealer-btn--sm" disabled={busy}>
            {t('dealer.orders.save_phone')}
          </button>
          <button type="button" className="dealer-btn dealer-btn--secondary dealer-btn--sm" onClick={cancel} disabled={busy}>
            {t('dealer.orders.cancel_edit')}
          </button>
        </span>
      )}
      {error && <span className="dealer-field-error">{error}</span>}
      {!compact && <span className="dealer-muted dealer-phone-editor__hint">{t('dealer.orders.phone_hint')}</span>}
    </form>
  )
}
